/**
 * CBS statistical divisions (6 districts / 15 sub-districts).
 * Boundary geometries: geoBoundaries ADM1/ADM2 (Wikimedia-derived, CC0),
 * aligned with Israeli CBS district / nafa structure.
 */

export type IsraelCbsBoundaryType = "district" | "sub_district";

export interface IsraelCbsRegionDefinition {
  /** Stable slug used in API responses. */
  code: string;
  /** Matches geoBoundaries `shapeName`. */
  shapeName: string;
  boundaryType: IsraelCbsBoundaryType;
  labelHe: string;
  sortOrder: number;
  /** Parent district code for sub-districts. */
  parentDistrictCode?: string;
}

export const ISRAEL_CBS_DISTRICTS: IsraelCbsRegionDefinition[] = [
  {
    code: "IL-JM",
    shapeName: "Jerusalem District",
    boundaryType: "district",
    labelHe: "מחוז ירושלים",
    sortOrder: 1,
  },
  {
    code: "IL-Z",
    shapeName: "Northern District",
    boundaryType: "district",
    labelHe: "מחוז הצפון",
    sortOrder: 2,
  },
  {
    code: "IL-HA",
    shapeName: "Haifa",
    boundaryType: "district",
    labelHe: "מחוז חיפה",
    sortOrder: 3,
  },
  {
    code: "IL-M",
    shapeName: "Central District",
    boundaryType: "district",
    labelHe: "מחוז המרכז",
    sortOrder: 4,
  },
  {
    code: "IL-TA",
    shapeName: "Tel Aviv",
    boundaryType: "district",
    labelHe: "מחוז תל אביב",
    sortOrder: 5,
  },
  {
    code: "IL-D",
    shapeName: "Southern District",
    boundaryType: "district",
    labelHe: "מחוז הדרום",
    sortOrder: 6,
  },
];

export const ISRAEL_CBS_SUB_DISTRICTS: IsraelCbsRegionDefinition[] = [
  {
    code: "golan",
    shapeName: "Golan",
    boundaryType: "sub_district",
    labelHe: "נפת רמת הגולן",
    sortOrder: 1,
    parentDistrictCode: "IL-Z",
  },
  {
    code: "zefat",
    shapeName: "Zefat",
    boundaryType: "sub_district",
    labelHe: "נפת צפת",
    sortOrder: 2,
    parentDistrictCode: "IL-Z",
  },
  {
    code: "akko",
    shapeName: "Akko",
    boundaryType: "sub_district",
    labelHe: "נפת עכו",
    sortOrder: 3,
    parentDistrictCode: "IL-Z",
  },
  {
    code: "kinneret",
    shapeName: "Kinneret",
    boundaryType: "sub_district",
    labelHe: "נפת כנרת",
    sortOrder: 4,
    parentDistrictCode: "IL-Z",
  },
  {
    code: "yizreel",
    shapeName: "Yizre'el",
    boundaryType: "sub_district",
    labelHe: "נפת יזרעאל",
    sortOrder: 5,
    parentDistrictCode: "IL-Z",
  },
  {
    code: "haifa",
    shapeName: "Haifa",
    boundaryType: "sub_district",
    labelHe: "נפת חיפה",
    sortOrder: 6,
    parentDistrictCode: "IL-HA",
  },
  {
    code: "hadera",
    shapeName: "Hadera",
    boundaryType: "sub_district",
    labelHe: "נפת חדרה",
    sortOrder: 7,
    parentDistrictCode: "IL-HA",
  },
  {
    code: "hasharon",
    shapeName: "HaSharon",
    boundaryType: "sub_district",
    labelHe: "נפת השרון",
    sortOrder: 8,
    parentDistrictCode: "IL-M",
  },
  {
    code: "petah-tiqwa",
    shapeName: "Petah Tiqwa",
    boundaryType: "sub_district",
    labelHe: "נפת פתח תקווה",
    sortOrder: 9,
    parentDistrictCode: "IL-M",
  },
  {
    code: "ramla",
    shapeName: "Ramla",
    boundaryType: "sub_district",
    labelHe: "נפת רמלה",
    sortOrder: 10,
    parentDistrictCode: "IL-M",
  },
  {
    code: "rehovot",
    shapeName: "Rehovot",
    boundaryType: "sub_district",
    labelHe: "נפת רחובות",
    sortOrder: 11,
    parentDistrictCode: "IL-M",
  },
  {
    code: "tel-aviv",
    shapeName: "Tel Aviv",
    boundaryType: "sub_district",
    labelHe: "נפת תל אביב",
    sortOrder: 12,
    parentDistrictCode: "IL-TA",
  },
  {
    code: "jerusalem",
    shapeName: "Jerusalem",
    boundaryType: "sub_district",
    labelHe: "נפת ירושלים",
    sortOrder: 13,
    parentDistrictCode: "IL-JM",
  },
  {
    code: "ashqelon",
    shapeName: "Ashqelon",
    boundaryType: "sub_district",
    labelHe: "נפת אשקלון",
    sortOrder: 14,
    parentDistrictCode: "IL-D",
  },
  {
    code: "beer-sheva",
    shapeName: "Be'er Sheva",
    boundaryType: "sub_district",
    labelHe: "נפת באר שבע",
    sortOrder: 15,
    parentDistrictCode: "IL-D",
  },
];

const districtByShapeName = new Map(
  ISRAEL_CBS_DISTRICTS.map((d) => [d.shapeName, d]),
);
const subDistrictByShapeName = new Map(
  ISRAEL_CBS_SUB_DISTRICTS.map((d) => [d.shapeName, d]),
);

export function getIsraelCbsDistrictByShapeName(shapeName: string) {
  return districtByShapeName.get(shapeName);
}

export function getIsraelCbsSubDistrictByShapeName(shapeName: string) {
  return subDistrictByShapeName.get(shapeName);
}

export const ISRAEL_CBS_BOUNDARY_ATTRIBUTION =
  "גבולות מחוזות/נפות: geoBoundaries (Wikimedia/CC0), מבנה CBS";

export const OPPORTUNITY_ADMIN_REGION_JERUSALEM_CODES = ["IL-JM"] as const;
export const OPPORTUNITY_ADMIN_REGION_COASTAL_CODES = [
  "IL-TA",
  "IL-M",
  "IL-HA",
] as const;
